import { useState, useRef, useEffect } from 'react';
import { Card, CardHeader, CardContent, CardFooter, Button } from '../../../shared/components/ui';
import { useConsumptionData, useUploadData, useDeleteConsumptionData, useCurrentProject } from '../../../hooks/useProjectData';
import { Upload, FileText, CheckCircle, AlertCircle, TrendingUp, BarChart3, Clock, X, ChevronDown, ChevronUp, Download } from 'lucide-react';
import { clsx } from 'clsx';
import { Line, Bar } from 'react-chartjs-2';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useFileUpload } from '../../../shared/hooks/useFileUpload';
import { formatNumber } from '../../../shared/utils/uploadHelpers';
import { ValidationTable } from '../../../shared/components/ValidationTable';
import {
  generateStandardExcelTemplate,
  generateStandardExcel15MinTemplate,
  generateGaribaldiCSVTemplate,
  generateSampleDataCSV
} from '../../../shared/utils/templateGenerator';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

type FormatType = 'standard_excel' | 'garibaldi_csv' | 'auto';
type ChartPeriod = 'day' | 'week' | 'month' | 'year';

interface ConsumptionUploadProps {
  projectId?: string;
}

export function ConsumptionUploadEnhanced({ projectId }: ConsumptionUploadProps) {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // File upload hook
  const fileUpload = useFileUpload({
    multiple: true,
    acceptedTypes: ['.csv', '.xlsx'],
    maxSizeMB: 50,
  });

  // Data hooks
  const { data: consumption, isLoading: loadingData, refetch: refetchConsumption } = useConsumptionData(projectId);
  const uploadMutation = useUploadData(projectId, (progress, step) => {
    fileUpload.setUploadProgress(progress);
    fileUpload.setUploadStep(step);
  });
  const deleteConsumptionMutation = useDeleteConsumptionData(projectId);
  const { data: currentProject } = useCurrentProject();

  // Local state
  const [formatType, setFormatType] = useState<FormatType>('auto');
  const [showCharts, setShowCharts] = useState(false);
  const [chartPeriod, setChartPeriod] = useState<ChartPeriod>('day');
  const [showStatistics, setShowStatistics] = useState(false);
  const [validation, setValidation] = useState<any | null>(null);

  // Load persisted validation from project data - always set (even to null) to clear on project switch
  useEffect(() => {
    setValidation(currentProject?.upload_validation_consumption || null);
  }, [currentProject]);

  const processFiles = async () => {
    if (fileUpload.files.length === 0 || !projectId) {
      fileUpload.setUploadError(t('consumptionUpload.errors.selectProject'));
      return;
    }

    fileUpload.setUploadError(null);
    fileUpload.setUploadProgress(0);
    fileUpload.setUploadStep(t('consumptionUpload.upload.startingUpload'));

    try {
      // Send all files together in a single request
      // This allows the backend to process them together and merge the data
      const result = await uploadMutation.mutateAsync({
        files: fileUpload.files,  // Send all files at once
        type: 'consumption'
      });

      // Capture validation data from backend response
      if (result?.validation) {
        setValidation(result.validation);
      }

      fileUpload.clearFiles();
      setShowCharts(true);
      toast.success(t('consumptionUpload.toast.uploadSuccess'));
    } catch (error) {
      console.error('Error processing consumption data:', error);
      fileUpload.setUploadError(error instanceof Error ? error.message : t('consumptionUpload.errors.processingFailed'));
    } finally {
      fileUpload.setUploadProgress(0);
      fileUpload.setUploadStep('');
    }
  };

  const clearData = async () => {
    try {
      // Clear consumption data from backend
      await deleteConsumptionMutation.mutateAsync();

      // Clear local state
      fileUpload.clearFiles();
      setShowCharts(false);
      setValidation(null);  // Clear validation table
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      // Wait a tick for cache to clear
      await new Promise(resolve => setTimeout(resolve, 100));

      // Force immediate refetch to get fresh empty data
      await refetchConsumption();

      toast.success(t('consumptionUpload.toast.clearSuccess'));
    } catch (error) {
      console.error('Error clearing consumption data:', error);
      toast.error(t('consumptionUpload.toast.clearError'));
    }
  };

  // Prepare chart data with proper averaging
  const getChartData = () => {
    if (!consumption?.data) return null;

    let labels: string[] = [];
    let data: number[] = [];

    if (chartPeriod === 'day') {
      // Show average day (24 hours)
      const hourlyAverages = new Array(24).fill(0);
      const hourlyCounts = new Array(24).fill(0);

      consumption.data.forEach(d => {
        const hour = new Date(d.timestamp).getHours();
        hourlyAverages[hour] += d.consumption;
        hourlyCounts[hour]++;
      });

      labels = Array.from({length: 24}, (_, i) => `${i}:00`);
      data = hourlyAverages.map((sum, i) => hourlyCounts[i] > 0 ? sum / hourlyCounts[i] : 0);

    } else if (chartPeriod === 'week') {
      // Show average week (Monday to Sunday)
      const dayNames = [
        t('consumptionUpload.charts.days.monday'),
        t('consumptionUpload.charts.days.tuesday'),
        t('consumptionUpload.charts.days.wednesday'),
        t('consumptionUpload.charts.days.thursday'),
        t('consumptionUpload.charts.days.friday'),
        t('consumptionUpload.charts.days.saturday'),
        t('consumptionUpload.charts.days.sunday')
      ];
      const dailyAverages = new Array(7).fill(0);
      const dailyCounts = new Array(7).fill(0);

      consumption.data.forEach(d => {
        const date = new Date(d.timestamp);
        const dayOfWeek = date.getDay() === 0 ? 6 : date.getDay() - 1; // Convert Sunday=0 to Monday=0
        dailyAverages[dayOfWeek] += d.consumption;
        dailyCounts[dayOfWeek]++;
      });

      labels = dayNames;
      data = dailyAverages.map((sum, i) => dailyCounts[i] > 0 ? (sum / dailyCounts[i]) * 24 : 0); // Daily total

    } else if (chartPeriod === 'month') {
      // Show average month pattern (days 1-31)
      const dayAverages = new Array(31).fill(0);
      const dayCounts = new Array(31).fill(0);

      consumption.data.forEach(d => {
        const date = new Date(d.timestamp);
        const dayOfMonth = date.getDate() - 1; // 0-indexed
        if (dayOfMonth < 31) {
          dayAverages[dayOfMonth] += d.consumption;
          dayCounts[dayOfMonth]++;
        }
      });

      labels = Array.from({length: 31}, (_, i) => (i + 1).toString());
      data = dayAverages.map((sum, i) => dayCounts[i] > 0 ? (sum / dayCounts[i]) * 24 : 0); // Daily total

    } else if (chartPeriod === 'year') {
      // Show complete year with all available data by month
      const monthlyData: { [key: string]: number } = {};

      consumption.data.forEach(d => {
        const date = new Date(d.timestamp);
        const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

        if (!monthlyData[monthKey]) {
          monthlyData[monthKey] = 0;
        }
        monthlyData[monthKey] += d.consumption;
      });

      // Only include months with data
      const sortedMonths = Object.keys(monthlyData).filter(key => monthlyData[key] > 0).sort();

      let prevYear: string | null = null;
      labels = sortedMonths.map((key, index) => {
        const [year, month] = key.split('-');
        const monthName = new Date(parseInt(year), parseInt(month) - 1).toLocaleDateString('en', { month: 'short' });

        // Show year for first month, January, and when year changes
        const showYear = index === 0 || month === '01' || year !== prevYear;
        prevYear = year;

        return showYear ? `${monthName} '${year.slice(-2)}` : monthName;
      });
      data = sortedMonths.map(key => monthlyData[key]);
    }

    return {
      labels,
      datasets: [{
        label: t('consumptionUpload.charts.consumptionLabel'),
        data,
        borderColor: '#37ade3',
        backgroundColor: 'rgba(55, 173, 227, 0.1)',
        fill: true,
        tension: 0.3,
      }],
    };
  };

  const getPowerChartData = () => {
    if (!consumption?.data15min && !consumption?.data) return null;

    const dataSource = consumption.data15min || consumption.data;
    let labels: string[] = [];
    let data: number[] = [];

    if (chartPeriod === 'day') {
      // Show average day (24 hours) - average power per hour
      const hourlySum = new Array(24).fill(0);
      const hourlyCounts = new Array(24).fill(0);

      dataSource.forEach(d => {
        const date = new Date('fecha' in d ? d.fecha : d.timestamp);
        const hour = date.getHours();
        const power = 'd' in d && 'consumo_kw' in d ? d.consumo_kw : 'power' in d ? d.power : 'value' in d ? d.value : 0;
        hourlySum[hour] += power;
        hourlyCounts[hour]++;
      });

      labels = Array.from({length: 24}, (_, i) => `${i}:00`);
      data = hourlySum.map((sum, i) => hourlyCounts[i] > 0 ? sum / hourlyCounts[i] : 0);

    } else if (chartPeriod === 'week') {
      // Show average week (Monday to Sunday) - max power per day
      const dayNames = [
        t('consumptionUpload.charts.days.monday'),
        t('consumptionUpload.charts.days.tuesday'),
        t('consumptionUpload.charts.days.wednesday'),
        t('consumptionUpload.charts.days.thursday'),
        t('consumptionUpload.charts.days.friday'),
        t('consumptionUpload.charts.days.saturday'),
        t('consumptionUpload.charts.days.sunday')
      ];
      const dailyMax = new Array(7).fill(0);
      const dailyCounts = new Array(7).fill(0);

      dataSource.forEach(d => {
        const date = new Date('fecha' in d ? d.fecha : d.timestamp);
        const dayOfWeek = date.getDay() === 0 ? 6 : date.getDay() - 1; // Convert Sunday=0 to Monday=0
        const power = 'd' in d && 'consumo_kw' in d ? d.consumo_kw : 'power' in d ? d.power : 'value' in d ? d.value : 0;
        dailyMax[dayOfWeek] = Math.max(dailyMax[dayOfWeek], power);
        dailyCounts[dayOfWeek]++;
      });

      labels = dayNames;
      data = dailyMax;

    } else if (chartPeriod === 'month') {
      // Show last 30 days - max power per day
      const dailyMax: { [key: string]: number } = {};
      const now = new Date();
      const thirtyDaysAgo = new Date(now.getTime() - (30 * 24 * 60 * 60 * 1000));

      // Initialize all 30 days with 0
      for (let i = 0; i < 30; i++) {
        const date = new Date(thirtyDaysAgo.getTime() + (i * 24 * 60 * 60 * 1000));
        const dateKey = date.toISOString().split('T')[0];
        dailyMax[dateKey] = 0;
      }

      // Get max power for each day
      dataSource.forEach(d => {
        const date = new Date('fecha' in d ? d.fecha : d.timestamp);
        const dateKey = date.toISOString().split('T')[0];
        if (dateKey in dailyMax) {
          const power = 'd' in d && 'consumo_kw' in d ? d.consumo_kw : 'power' in d ? d.power : 'value' in d ? d.value : 0;
          dailyMax[dateKey] = Math.max(dailyMax[dateKey], power);
        }
      });

      const sortedDays = Object.keys(dailyMax).sort();
      labels = sortedDays.map(key => {
        const date = new Date(key);
        return date.getDate().toString();
      });
      data = sortedDays.map(key => dailyMax[key]);

    } else if (chartPeriod === 'year') {
      // Show complete year - max power per month
      const monthlyMax: { [key: string]: number } = {};

      dataSource.forEach(d => {
        const date = new Date('fecha' in d ? d.fecha : d.timestamp);
        const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        const power = 'd' in d && 'consumo_kw' in d ? d.consumo_kw : 'power' in d ? d.power : 'value' in d ? d.value : 0;

        if (!monthlyMax[monthKey]) {
          monthlyMax[monthKey] = 0;
        }
        monthlyMax[monthKey] = Math.max(monthlyMax[monthKey], power);
      });

      // Only include months with data
      const sortedMonths = Object.keys(monthlyMax).filter(key => monthlyMax[key] > 0).sort();

      let prevYear: string | null = null;
      labels = sortedMonths.map((key, index) => {
        const [year, month] = key.split('-');
        const monthName = new Date(parseInt(year), parseInt(month) - 1).toLocaleDateString('en', { month: 'short' });

        // Show year for first month, January, and when year changes
        const showYear = index === 0 || month === '01' || year !== prevYear;
        prevYear = year;

        return showYear ? `${monthName} '${year.slice(-2)}` : monthName;
      });
      data = sortedMonths.map(key => monthlyMax[key]);
    }

    return {
      labels,
      datasets: [{
        label: t('consumptionUpload.charts.powerLabel'),
        data,
        backgroundColor: '#37ade3',
        borderColor: '#37ade3',
        borderWidth: 1,
      }],
    };
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: (context: { dataset: { label?: string }; parsed: { y: number } }) => {
            const label = context.dataset.label || '';
            const value = formatNumber(context.parsed.y);
            return `${label}: ${value}`;
          },
        },
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          maxRotation: 45,
          minRotation: 0,
          autoSkip: true,
          maxTicksLimit: chartPeriod === 'day' ? 12 : chartPeriod === 'week' ? 7 : chartPeriod === 'month' ? 12 : 24,
        },
      },
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.05)',
        },
        ticks: {
          callback: (value: any) => formatNumber(value),
        },
      },
    },
    elements: {
      point: {
        radius: chartPeriod === 'day' ? 2 : chartPeriod === 'week' ? 4 : 3,
        hoverRadius: chartPeriod === 'day' ? 4 : chartPeriod === 'week' ? 6 : 5,
      },
      line: {
        borderWidth: 2,
      },
    },
  };

  const isLoading = uploadMutation.isPending || loadingData || deleteConsumptionMutation.isPending;

  return (
    <div className="space-y-6">
      <Card variant={consumption ? 'elevated' : 'default'}>
        <CardHeader>
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-swiss-charcoal">
              {t('consumptionUpload.title')}
            </h3>
            {consumption && (
              <CheckCircle className="w-5 h-5 text-green-600" />
            )}
          </div>
        </CardHeader>

        <CardContent className="p-6 space-y-4">
          {!consumption ? (
            <>
              {/* Format Selection */}
              <div className="flex gap-2 p-3 bg-swiss-pearl rounded-lg">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="radio"
                    name="format"
                    value="auto"
                    checked={formatType === 'auto'}
                    onChange={(e) => setFormatType(e.target.value as FormatType)}
                    className="text-brand-primary"
                  />
                  <span>{t('consumptionUpload.format.autoDetect')}</span>
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="radio"
                    name="format"
                    value="standard_excel"
                    checked={formatType === 'standard_excel'}
                    onChange={(e) => setFormatType(e.target.value as FormatType)}
                    className="text-brand-primary"
                  />
                  <span>{t('consumptionUpload.format.standardExcel')}</span>
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="radio"
                    name="format"
                    value="garibaldi_csv"
                    checked={formatType === 'garibaldi_csv'}
                    onChange={(e) => setFormatType(e.target.value as FormatType)}
                    className="text-brand-primary"
                  />
                  <span>{t('consumptionUpload.format.garibaldiCsv')}</span>
                </label>
              </div>

              {/* Template Download Section */}
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <Download className="w-5 h-5 text-brand-primary mt-0.5 flex-shrink-0" />
                  <div className="flex-1 space-y-2">
                    <div className="text-sm font-medium text-swiss-charcoal">
                      {t('consumptionUpload.templates.title')}
                    </div>
                    <div className="text-xs text-swiss-stone">
                      {t('consumptionUpload.templates.description')}
                    </div>
                    <div className="flex flex-wrap gap-2 mt-2">
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={generateStandardExcelTemplate}
                        className="text-xs"
                      >
                        <Download className="w-3 h-3 mr-1" />
                        {t('consumptionUpload.templates.excelHourly')}
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={generateStandardExcel15MinTemplate}
                        className="text-xs"
                      >
                        <Download className="w-3 h-3 mr-1" />
                        {t('consumptionUpload.templates.excel15Min')}
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={generateGaribaldiCSVTemplate}
                        className="text-xs"
                      >
                        <Download className="w-3 h-3 mr-1" />
                        {t('consumptionUpload.templates.garibaldiCsv')}
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={generateSampleDataCSV}
                        className="text-xs"
                      >
                        <Download className="w-3 h-3 mr-1" />
                        {t('consumptionUpload.templates.sampleData')}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>

              {/* File Upload Zone */}
              <div
                className={clsx(
                  'border-2 border-dashed rounded-lg p-6 text-center transition-colors',
                  'hover:border-brand-primary hover:bg-brand-primary/5',
                  fileUpload.files.length > 0 ? 'border-brand-primary bg-brand-primary/5' : 'border-swiss-mist'
                )}
                onDrop={fileUpload.handleDrop}
                onDragOver={fileUpload.handleDragOver}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  id="consumption-files"
                  accept=".csv,.xlsx"
                  multiple
                  onChange={fileUpload.handleFileSelect}
                  className="hidden"
                />
                <label
                  htmlFor="consumption-files"
                  className="cursor-pointer space-y-2"
                >
                  <Upload className="w-8 h-8 mx-auto text-swiss-stone" />
                  <p className="text-sm text-swiss-charcoal">
                    {t('consumptionUpload.upload.clickOrDrag')}
                  </p>
                  <p className="text-xs text-swiss-stone">
                    {t('consumptionUpload.upload.multipleFiles')}
                  </p>
                </label>
              </div>

              {/* File List */}
              {fileUpload.files.length > 0 && (
                <div className="space-y-2">
                  <div className="text-xs font-semibold text-swiss-charcoal">
                    {t('consumptionUpload.upload.selectedFiles', { count: fileUpload.files.length })}
                  </div>
                  {fileUpload.files.map((file, index) => (
                    <div key={index} className="flex items-center gap-2 p-2 bg-swiss-pearl rounded-lg">
                      <FileText className="w-4 h-4 text-brand-primary" />
                      <span className="text-sm text-swiss-charcoal flex-1">{file.name}</span>
                      <span className="text-xs text-swiss-stone">{(file.size / 1024).toFixed(1)} KB</span>
                      <button
                        onClick={() => fileUpload.handleRemoveFile(index)}
                        className="text-swiss-stone hover:text-red-500"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {fileUpload.uploadError && (
                <div className="flex items-center gap-2 p-3 bg-red-50 text-red-700 rounded-lg">
                  <AlertCircle className="w-4 h-4" />
                  <span className="text-sm">{fileUpload.uploadError}</span>
                </div>
              )}

              {/* Upload Progress Bar */}
              {uploadMutation.isPending && fileUpload.uploadProgress > 0 && (
                <div className="space-y-2 p-4 bg-brand-primary/5 rounded-lg border border-brand-primary/20">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-swiss-charcoal font-medium">{fileUpload.uploadStep}</span>
                    <span className="text-brand-primary font-semibold">{fileUpload.uploadProgress}%</span>
                  </div>
                  <div className="w-full h-2 bg-swiss-pearl rounded-full overflow-hidden">
                    <div
                      className="h-full bg-brand-primary transition-all duration-300 ease-out"
                      style={{ width: `${fileUpload.uploadProgress}%` }}
                    />
                  </div>
                </div>
              )}

              {/* Validation Table */}
              {validation && <ValidationTable validation={validation} type="consumption" />}
            </>
          ) : (
            <div className="space-y-4">
              {/* Summary Statistics */}
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-swiss-pearl rounded-lg">
                  <div className="flex items-center gap-2 mb-1">
                    <TrendingUp className="w-4 h-4 text-brand-primary" />
                    <span className="text-xs text-swiss-stone">{t('consumptionUpload.statistics.totalRecords')}</span>
                  </div>
                  <div className="text-lg font-semibold text-swiss-charcoal">
                    {consumption.totalRecords.toLocaleString()}
                  </div>
                </div>

                <div className="p-3 bg-swiss-pearl rounded-lg">
                  <div className="flex items-center gap-2 mb-1">
                    <BarChart3 className="w-4 h-4 text-brand-primary" />
                    <span className="text-xs text-swiss-stone">{t('consumptionUpload.statistics.monthlyAvgConsumption')}</span>
                  </div>
                  <div className="text-lg font-semibold text-swiss-charcoal">
                    {formatNumber(consumption.consumptionPatterns?.avg_monthly_kwh || (consumption.avgConsumption * 30 * 24))} kWh
                  </div>
                </div>

                <div className="p-3 bg-swiss-pearl rounded-lg">
                  <div className="flex items-center gap-2 mb-1">
                    <TrendingUp className="w-4 h-4 text-red-500" />
                    <span className="text-xs text-swiss-stone">{t('consumptionUpload.statistics.peakDemand')}</span>
                  </div>
                  <div className="text-lg font-semibold text-swiss-charcoal">
                    {(consumption.peakDemand ?? 0).toFixed(2)} kW
                  </div>
                </div>

                <div className="p-3 bg-swiss-pearl rounded-lg">
                  <div className="flex items-center gap-2 mb-1">
                    <Clock className="w-4 h-4 text-green-600" />
                    <span className="text-xs text-swiss-stone">{t('consumptionUpload.statistics.loadFactor')}</span>
                  </div>
                  <div className="text-lg font-semibold text-swiss-charcoal">
                    {(consumption.loadFactor ?? 0).toFixed(1)}%
                  </div>
                </div>
              </div>

              {/* Date Range */}
              <div className="flex justify-between text-sm p-3 bg-swiss-pearl rounded-lg">
                <span className="text-swiss-stone">{t('consumptionUpload.statistics.period')}:</span>
                <span className="font-medium text-swiss-charcoal">
                  {new Date(consumption.startDate).toLocaleDateString()} - {new Date(consumption.endDate).toLocaleDateString()}
                </span>
              </div>

              {/* Comprehensive Statistics - Collapsible */}
              {consumption.consumptionPatterns && (
                <div className="space-y-2 mt-3">
                  <button
                    onClick={() => setShowStatistics(!showStatistics)}
                    className="flex items-center gap-2 w-full text-sm font-semibold text-swiss-charcoal hover:text-swiss-stone transition-colors"
                  >
                    <span>{t('consumptionUpload.statistics.consumptionStatistics')}</span>
                    {showStatistics ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </button>

                  {showStatistics && (
                    <div className="space-y-3">
                      {/* Time-based Averages */}
                      <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="p-2 bg-swiss-pearl rounded">
                      <span className="text-swiss-stone">{t('consumptionUpload.statistics.avgHourly')}:</span>
                      <div className="font-semibold">{formatNumber(consumption.consumptionPatterns.avg_hourly_kwh || 0)} kWh</div>
                    </div>
                    <div className="p-2 bg-swiss-pearl rounded">
                      <span className="text-swiss-stone">{t('consumptionUpload.statistics.avgDaily')}:</span>
                      <div className="font-semibold">{formatNumber(consumption.consumptionPatterns.avg_daily_kwh || 0)} kWh</div>
                    </div>
                    <div className="p-2 bg-swiss-pearl rounded">
                      <span className="text-swiss-stone">{t('consumptionUpload.statistics.avgMonthly')}:</span>
                      <div className="font-semibold">{formatNumber(consumption.consumptionPatterns.avg_monthly_kwh || 0)} kWh</div>
                    </div>
                    <div className="p-2 bg-swiss-pearl rounded">
                      <span className="text-swiss-stone">{t('consumptionUpload.statistics.annualTotal')}:</span>
                      <div className="font-semibold">{formatNumber(consumption.consumptionPatterns.total_year_kwh || 0)} kWh</div>
                    </div>
                  </div>

                  {/* Day/Night Pattern */}
                  <div className="space-y-1">
                    <div className="text-xs font-semibold text-swiss-stone">{t('consumptionUpload.statistics.dayNightPattern')}</div>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div className="p-2 bg-swiss-pearl rounded">
                        <span className="text-swiss-stone">{t('consumptionUpload.statistics.day')}:</span>
                        <div className="font-semibold">{formatNumber(consumption.consumptionPatterns.day_night_avg_kw?.['Day (6-18)'] || 0)} kW</div>
                      </div>
                      <div className="p-2 bg-swiss-pearl rounded">
                        <span className="text-swiss-stone">{t('consumptionUpload.statistics.night')}:</span>
                        <div className="font-semibold">{formatNumber(consumption.consumptionPatterns.day_night_avg_kw?.['Night (18-6)'] || 0)} kW</div>
                      </div>
                    </div>
                  </div>

                  {/* Weekend vs Weekday */}
                  <div className="space-y-1">
                    <div className="text-xs font-semibold text-swiss-stone">{t('consumptionUpload.statistics.weekendVsWeekday')}</div>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div className="p-2 bg-swiss-pearl rounded">
                        <span className="text-swiss-stone">{t('consumptionUpload.statistics.weekdayAvg')}:</span>
                        <div className="font-semibold">{formatNumber(consumption.consumptionPatterns.weekday_avg_kw || 0)} kW</div>
                      </div>
                      <div className="p-2 bg-swiss-pearl rounded">
                        <span className="text-swiss-stone">{t('consumptionUpload.statistics.weekendAvg')}:</span>
                        <div className="font-semibold">{formatNumber(consumption.consumptionPatterns.weekend_avg_kw || 0)} kW</div>
                      </div>
                    </div>
                  </div>

                  {/* Peak vs Off-Peak */}
                  <div className="space-y-1">
                    <div className="text-xs font-semibold text-swiss-stone">{t('consumptionUpload.statistics.peakHoursAnalysis')}</div>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div className="p-2 bg-swiss-pearl rounded">
                        <span className="text-swiss-stone">{t('consumptionUpload.statistics.peak')}:</span>
                        <div className="font-semibold">{formatNumber(consumption.consumptionPatterns.peak_hours_avg_kw || 0)} kW</div>
                      </div>
                      <div className="p-2 bg-swiss-pearl rounded">
                        <span className="text-swiss-stone">{t('consumptionUpload.statistics.offPeak')}:</span>
                        <div className="font-semibold">{formatNumber(consumption.consumptionPatterns.off_peak_avg_kw || 0)} kW</div>
                      </div>
                    </div>
                  </div>
                </div>
                  )}
                </div>
              )}

              {/* Data Sample Preview */}
              {consumption.dataSample && (
                <div className="space-y-2">
                  <div className="text-xs font-semibold text-swiss-charcoal">{t('consumptionUpload.statistics.dataPreview')}</div>
                  <div className="text-xs space-y-1 p-2 bg-swiss-pearl rounded font-mono">
                    {consumption.dataSample.first_rows.slice(0, 3).map((row, i) => (
                      <div key={i}>
                        {row.date} {row.time}: {row.value.toFixed(2)} kW
                      </div>
                    ))}
                    <div>...</div>
                    {consumption.dataSample.last_rows.slice(-2).map((row, i) => (
                      <div key={i}>
                        {row.date} {row.time}: {row.value.toFixed(2)} kW
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>

        <CardFooter>
          {!consumption ? (
            <Button
              className="w-full"
              onClick={processFiles}
              disabled={fileUpload.files.length === 0 || isLoading}
              loading={isLoading}
            >
              {t('consumptionUpload.buttons.processData')}
            </Button>
          ) : (
            <div className="flex gap-2 w-full">
              <Button
                className="flex-1"
                variant="secondary"
                onClick={clearData}
                disabled={deleteConsumptionMutation.isPending}
                loading={deleteConsumptionMutation.isPending}
              >
                {t('consumptionUpload.buttons.replaceFiles')}
              </Button>
              <Button
                className="flex-1"
                variant="ghost"
                onClick={() => setShowCharts(!showCharts)}
              >
                {showCharts ? t('consumptionUpload.buttons.hideCharts') : t('consumptionUpload.buttons.showCharts')}
              </Button>
            </div>
          )}
        </CardFooter>
      </Card>

      {/* Charts Section */}
      {consumption && showCharts && (
        <>
          {/* Consumption Chart */}
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between w-full">
                <div className="flex items-center gap-2">
                  <h4 className="text-md font-semibold text-swiss-charcoal">
                    {t('consumptionUpload.charts.energyConsumptionTrend')}
                  </h4>
                  {(chartPeriod === 'day' || chartPeriod === 'week' || chartPeriod === 'month') && (
                    <span className="text-xs text-swiss-stone bg-swiss-pearl px-2 py-0.5 rounded">{t('consumptionUpload.charts.avg')}</span>
                  )}
                </div>
                <div className="flex gap-1">
                  {(['day', 'week', 'month', 'year'] as ChartPeriod[]).map(period => (
                    <button
                      key={period}
                      onClick={() => setChartPeriod(period)}
                      className={clsx(
                        'px-3 py-1 text-xs rounded transition-colors',
                        chartPeriod === period
                          ? 'bg-brand-primary text-white'
                          : 'bg-swiss-pearl text-swiss-charcoal hover:bg-swiss-mist'
                      )}
                    >
                      {t(`consumptionUpload.charts.periods.${period}`)}
                    </button>
                  ))}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                {getChartData() && (
                  <Line data={getChartData()!} options={chartOptions} />
                )}
              </div>
            </CardContent>
          </Card>

          {/* Power Demand Chart */}
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between w-full">
                <div className="flex items-center gap-2">
                  <h4 className="text-md font-semibold text-swiss-charcoal">
                    {t('consumptionUpload.charts.powerDemandProfile')}
                  </h4>
                  {chartPeriod === 'day' && (
                    <span className="text-xs text-swiss-stone bg-swiss-pearl px-2 py-0.5 rounded">{t('consumptionUpload.charts.avgPerHour')}</span>
                  )}
                </div>
                <div className="flex gap-1">
                  {(['day', 'week', 'month', 'year'] as ChartPeriod[]).map(period => (
                    <button
                      key={period}
                      onClick={() => setChartPeriod(period)}
                      className={clsx(
                        'px-3 py-1 text-xs rounded transition-colors',
                        chartPeriod === period
                          ? 'bg-brand-primary text-white'
                          : 'bg-swiss-pearl text-swiss-charcoal hover:bg-swiss-mist'
                      )}
                    >
                      {t(`consumptionUpload.charts.periods.${period}`)}
                    </button>
                  ))}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                {getPowerChartData() && (
                  <Bar data={getPowerChartData()!} options={chartOptions} />
                )}
              </div>
            </CardContent>
          </Card>

          {/* Validation Table */}
          {validation && (
            <Card>
              <CardContent className="p-4">
                <ValidationTable validation={validation} type="consumption" />
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
